/// <reference types="google.maps" />

import { Injectable } from '@angular/core';
import type {
    Coordinate,
    MarkerConfig,
    ShuttleState,
    RouteConfig,
    PolylineConfig
} from './google-map.types';

/**
 * Service for managing Google Map markers, routes, and shuttle animations
 * Handles marker lifecycle, shuttle position updates with rotation, and route calculation
 */
@Injectable({
    providedIn: 'root'
})
export class GoogleMapService {
    private markers: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map();
    private shuttleStates: Map<string, ShuttleState> = new Map();
    private polylines: google.maps.Polyline[] = [];
    private directionsRenderers: google.maps.DirectionsRenderer[] = [];

    // Constants for marker configuration
    private readonly MARKER_CONFIG = {
        SIZE: 32,
        LETTER_COLOR: '#EA4335',
        LETTER_HOVER_COLOR: '#C5221F'
    } as const;

    constructor() {}

    // ================================================
    // Marker Management
    // ================================================

    /**
     * Add a marker to the map
     * Returns the created marker for imperative control
     */
    public addMarker(
        map: google.maps.Map,
        config: MarkerConfig,
        iconUrl?: string
    ): google.maps.marker.AdvancedMarkerElement {
        let content: HTMLElement;

        // Create appropriate marker content based on type
        switch (config.type) {
            case 'letter':
                content = this.createLetterMarkerContent(config.label || 'A');
                break;
            case 'shuttle':
                content = this.createShuttleMarkerContent(iconUrl || '', config.color);
                break;
            default:
                content = this.createIconMarkerContent(iconUrl || '', config.title || '');
        }

        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: new google.maps.LatLng(config.position.latitude, config.position.longitude),
            content: content,
            title: config.title,
            zIndex: config.zIndex ?? 10
        });

        this.markers.set(config.id, marker);

        // Initialize shuttle state if this is a shuttle marker
        if (config.type === 'shuttle') {
            this.shuttleStates.set(config.id, {
                element: marker,
                currentLat: config.position.latitude,
                currentLng: config.position.longitude,
                heading: 0
            });
        }

        return marker;
    }

    /**
     * Remove a marker from the map
     */
    public removeMarker(markerId: string): void {
        const marker = this.markers.get(markerId);
        if (marker) {
            marker.map = null;
            this.markers.delete(markerId);
        }

        // Clean up shuttle state if exists
        this.shuttleStates.delete(markerId);
    }

    /**
     * Remove all markers from the map
     */
    public clearAllMarkers(): void {
        for (const marker of this.markers.values()) {
            marker.map = null;
        }
        this.markers.clear();
        this.shuttleStates.clear();
    }

    /**
     * Get a marker by ID
     */
    public getMarker(markerId: string): google.maps.marker.AdvancedMarkerElement | undefined {
        return this.markers.get(markerId);
    }

    // ================================================
    // Shuttle Position & Rotation
    // ================================================

    /**
     * Update shuttle position with smooth CSS transition
     * Restarts animation from current position
     * @param shuttleId - Unique shuttle identifier
     * @param latitude - New latitude
     * @param longitude - New longitude
     * @param headingDegrees - Direction shuttle is facing (0-360, where 0=North, 90=East)
     */
    public updateShuttlePosition(
        shuttleId: string,
        latitude: number,
        longitude: number,
        headingDegrees: number
    ): void {
        const state = this.shuttleStates.get(shuttleId);
        if (!state) {
            // console.log(`Shuttle ${shuttleId} not found. Add marker first.`);
            return;
        }

        // Normalize heading to 0-360 range
        const normalizedHeading = ((headingDegrees % 360) + 360) % 360;

        // Update position (Google Maps handles the transition via CSS)
        state.element.position = new google.maps.LatLng(latitude, longitude);

        // Update shuttle state
        state.currentLat = latitude;
        state.currentLng = longitude;
        state.heading = normalizedHeading;

        // Apply rotation to marker content
        this.rotateShuttleMarker(shuttleId, normalizedHeading);
    }

    /**
     * Rotate shuttle marker to match heading direction
     * @param shuttleId - Unique shuttle identifier
     * @param headingDegrees - Direction in degrees (0=North, 90=East, 180=South, 270=West)
     */
    public rotateShuttleMarker(shuttleId: string, headingDegrees: number): void {
        const state = this.shuttleStates.get(shuttleId);
        if (!state) {
            // console.warn(`Shuttle ${shuttleId} not found.`);
            return;
        }

        const normalizedHeading = ((headingDegrees % 360) + 360) % 360;
        const content = state.element.content as HTMLElement;

        if (content) {
            // Apply rotation transform
            // Subtract 90 because marker icons typically point right (90°) by default
            const rotationAngle = normalizedHeading - 90;
            content.style.transform = `rotate(${rotationAngle}deg)`;
        }

        // Update state
        state.heading = normalizedHeading;
    }

    /**
     * Get current shuttle state
     */
    public getShuttleState(shuttleId: string): ShuttleState | undefined {
        return this.shuttleStates.get(shuttleId);
    }

    // ================================================
    // Route Calculation & Drawing
    // ================================================

    /**
     * Calculate and display a route between two points
     * Uses Google Maps Directions API
     */
    public async calculateRoute(
        map: google.maps.Map,
        config: RouteConfig
    ): Promise<google.maps.DirectionsResult> {
        const directionsService = new google.maps.DirectionsService();

        // Convert coordinates to addresses if needed
        const origin = this.convertToLatLngOrAddress(config.origin);
        const destination = this.convertToLatLngOrAddress(config.destination);

        const request: google.maps.DirectionsRequest = {
            origin: origin,
            destination: destination,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.IMPERIAL
        };

        const result = await this.getDirections(directionsService, request);

        // Render the route on the map
        const renderer = new google.maps.DirectionsRenderer({
            map: map,
            directions: result,
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: {
                strokeColor: config.color ?? '#4285F4',
                strokeWeight: config.weight ?? 4,
                strokeOpacity: config.opacity ?? 0.7
            }
        });

        this.directionsRenderers.push(renderer);

        return result;
    }

    /**
     * Draw a polyline on the map (for historical routes)
     */
    public drawPolyline(map: google.maps.Map, config: PolylineConfig): google.maps.Polyline {
        const path = config.path.map(
            coord => new google.maps.LatLng(coord.latitude, coord.longitude)
        );

        const polyline = new google.maps.Polyline({
            path: path,
            strokeColor: config.color,
            strokeWeight: config.weight ?? 4,
            strokeOpacity: config.opacity ?? 0.7,
            map: map
        });

        this.polylines.push(polyline);

        return polyline;
    }

    /**
     * Clear all routes from the map
     */
    public clearRoutes(): void {
        // Clear polylines
        for (const polyline of this.polylines) {
            polyline.setMap(null);
        }
        this.polylines = [];

        // Clear directions renderers
        for (const renderer of this.directionsRenderers) {
            renderer.setMap(null);
        }
        this.directionsRenderers = [];
    }

    // ================================================
    // Helper Methods - Private
    // ================================================

    /**
     * Create HTML content for icon-based marker
     */
    private createIconMarkerContent(iconUrl: string, title: string): HTMLElement {
        const markerDiv = document.createElement('div');
        markerDiv.className = 'icon-marker';
        markerDiv.title = title;
        markerDiv.style.setProperty('--marker-icon-url', `url(${iconUrl})`);

        return markerDiv;
    }

    /**
     * Create HTML content for letter marker (A, B, C, etc.)
     */
    private createLetterMarkerContent(letter: string): HTMLElement {
        const markerDiv = document.createElement('div');
        markerDiv.className = 'letter-marker';
        markerDiv.textContent = letter;

        return markerDiv;
    }

    /**
     * Create HTML content for shuttle marker with rotation support
     */
    private createShuttleMarkerContent(iconUrl: string, color?: string): HTMLElement {
        const markerDiv = document.createElement('div');
        markerDiv.className = 'shuttle-marker';
        markerDiv.style.setProperty('--marker-icon-url', `url(${iconUrl})`);

        if (color) {
            markerDiv.style.setProperty('--shuttle-color', color);
        }

        return markerDiv;
    }

    /**
     * Convert coordinate or address to format needed by Directions API
     */
    private convertToLatLngOrAddress(
        input: string | Coordinate
    ): string | google.maps.LatLng {
        if (typeof input === 'string') {
            return input;
        }

        return new google.maps.LatLng(input.latitude, input.longitude);
    }

    /**
     * Wrap Google Maps Directions API in a promise
     */
    private getDirections(
        service: google.maps.DirectionsService,
        request: google.maps.DirectionsRequest
    ): Promise<google.maps.DirectionsResult> {
        const promise = new Promise<google.maps.DirectionsResult>((resolve, reject) => {
            service.route(request, (response, status) => {
                if (status === google.maps.DirectionsStatus.OK && response) {
                    resolve(response);
                } else {
                    reject(new Error(`Route calculation failed: ${status}`));
                }
            });
        });

        return promise;
    }

    // ================================================
    // Cleanup
    // ================================================

    /**
     * Clean up all resources
     */
    public cleanup(): void {
        this.clearAllMarkers();
        this.clearRoutes();
    }
}
